import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Folder, MapPin, Truck } from 'lucide-react';

interface ConvertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConvertToBooking?: () => void;
  onConvertToMobileBooking?: () => void;
  onConvertToProject?: () => void;
  hasCalendarApp?: boolean;
  hasProjectsApp?: boolean;
  projectId?: string | null;
  bookingId?: string | null;
  showLocationBooking?: boolean;
  showMobileBooking?: boolean;
}

export default function ConvertModal({
  isOpen,
  onClose,
  onConvertToBooking,
  onConvertToMobileBooking,
  onConvertToProject,
  hasCalendarApp = false,
  hasProjectsApp = false,
  projectId = null,
  bookingId = null,
  showLocationBooking = true,
  showMobileBooking = true
}: ConvertModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleConvertToBooking = () => {
    if (onConvertToBooking) {
      onConvertToBooking();
    }
    onClose();
  };

  const handleConvertToMobileBooking = () => {
    if (onConvertToMobileBooking) {
      onConvertToMobileBooking();
    }
    onClose();
  };

  const handleConvertToProject = () => {
    if (onConvertToProject) {
      onConvertToProject();
    }
    onClose();
  };

  return createPortal(
    <>
      {isOpen && (
        <>
          {/* iOS-style backdrop with blur */}
          <div 
            className="fixed inset-0 transition-opacity duration-200"
            style={{
              zIndex: 100150,
              background: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              opacity: 1,
              pointerEvents: 'auto'
            }}
            onClick={onClose}
          />
          
          {/* Modal Content - iOS Style */}
          <div 
            className="fixed inset-0 flex items-center justify-center pointer-events-none"
            style={{ zIndex: 100160 }}
          >
            <div 
              className="bg-white rounded-[20px] shadow-xl max-w-md w-full mx-4 pointer-events-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Close button - top right corner */}
              <button 
                onClick={onClose} 
                className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <X className="h-5 w-5" />
              </button>

              {/* Content */}
              <div className="px-6 py-5">
                <div className="space-y-3">
                  {/* Convert to Existing Booking - when already linked */}
                  {hasCalendarApp && bookingId && (
                    <button
                      onClick={handleConvertToBooking}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                           style={{ 
                             backdropFilter: 'blur(10px)', 
                             WebkitBackdropFilter: 'blur(10px)'
                           }}>
                        <Calendar className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">Convert to Existing Booking</div>
                        <div className="text-xs text-gray-500 mt-0.5">Add services to the linked booking</div>
                      </div>
                    </button>
                  )}

                  {/* Convert to Location Booking */}
                  {hasCalendarApp && !bookingId && !projectId && showLocationBooking && (
                    <button
                      onClick={handleConvertToBooking}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                           style={{ 
                             backdropFilter: 'blur(10px)', 
                             WebkitBackdropFilter: 'blur(10px)'
                           }}>
                        <MapPin className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">Convert to Location Booking</div>
                        <div className="text-xs text-gray-500 mt-0.5">Create an in-shop booking from this proposal</div>
                      </div>
                    </button>
                  )}

                  {/* Convert to Mobile Booking */}
                  {hasCalendarApp && !bookingId && !projectId && showMobileBooking && (
                    <button
                      onClick={handleConvertToMobileBooking}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                           style={{ 
                             backdropFilter: 'blur(10px)', 
                             WebkitBackdropFilter: 'blur(10px)'
                           }}>
                        <Truck className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">Convert to Mobile Booking</div>
                        <div className="text-xs text-gray-500 mt-0.5">Create a mobile/on-site booking from this proposal</div>
                      </div>
                    </button>
                  )}
                  
                  {/* Convert to Project - Show if hasProjectsApp OR if already linked to a project */}
                  {(hasProjectsApp || projectId) && (
                    <button
                      onClick={handleConvertToProject}
                      className="w-full flex items-center gap-3 px-4 py-3.5 text-left bg-white border border-gray-200 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex-shrink-0 backdrop-blur-sm bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-full p-3 shadow-md flex items-center justify-center"
                           style={{ 
                             backdropFilter: 'blur(10px)', 
                             WebkitBackdropFilter: 'blur(10px)'
                           }}>
                        <Folder className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-900">
                          {projectId ? 'Convert to Existing Project' : 'Convert to New Project'}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {projectId ? 'Link this proposal to the existing project' : 'Create a new project from this proposal'}
                        </div>
                      </div>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>,
    document.body
  );
}
